import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Spin, Tag } from 'antd';
import styled from '@emotion/styled';

import _ from 'lodash';

import { antIcon } from 'components/pages/Login';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import { pricingSimulatorUrls } from 'config/urls';
import { getConfig, sendNotification, transformFiltersV2 } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import { UserContext } from 'contexts/ConfigContext';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import FilterRibbon from '../FilterRibbon/FilterRibbon';
import { DimensionDropdownType } from 'types/pricingSimulator';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { colorConstants } from 'config/colorConstants';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';

type Props = {
    open: boolean;
    onCancel: () => void;
    onSubmitFinish: (data: any) => void;
};

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
    .ant-modal-content {
        padding: 12px 16px;
        border-radius: 8px !important;
    }
    .ant-modal-header {
        padding: 8px 12px;

        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px 0 8px 0;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const DownloadTemplateModal: FC<Props> = ({ open, onCancel, onSubmitFinish }) => {
    const { selectedScenarioInfo } = useContext(pricingSimulatorDataContext);
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const { dim1DropdownList = [], dim2DropdownList = [], lowestDimLensNames = [] } = dimensionConfigData ?? {};
    const dimDropdownList = [...(dim1DropdownList ?? []), ...(dim2DropdownList ?? [])];
    const { appConfig } = useContext(UserContext);
    const { scenarioFilterOffsetLimit } = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const fixedDims = ['product', 'pvp_mkt_channel_retailer'];
    const metrics = useMemo(() => {
        return selectedScenarioInfo?.lenseConfig?.reduce(
            (acc: { set: Set<Number>; result: { label: string; value: string }[] }, item) => {
                item.attributes.forEach((attr) => {
                    if (attr.availableForBulkUpload && !acc.set.has(attr.attributeId)) {
                        acc.result.push({ label: attr.name, value: attr?.kpiKeyName ?? '' });
                        acc.set.add(attr.attributeId);
                    }
                });
                return acc;
            },
            { set: new Set<Number>(), result: [] },
        ).result;
    }, [selectedScenarioInfo?.lenseConfig])?.sort((a, b) => a.label.localeCompare(b.label));

    const scenarioFilterDimensionInfo = dimensionConfigData?.scenarioFilterDimensionInfo ?? {};
    const scenarioFilterKeys = dimensionConfigData?.scenarioFilterKeys ?? [];

    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);
    const [applyFilterObject, setApplyFilterObject] = useState<any>({});
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<any>({});
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState({});
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedDims, setSelectedDims] = useState(fixedDims);
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);
        const filters = transformFilterObjectWithDimensionsInfo(resultObj.applyFilterObject, scenarioFilterKeys);
        const payload = { filters, scenario_id: selectedScenarioInfo?.scenarioId };
        // getScenarioDataCountMutation({ ...payload });
    };

    useEffect(() => {
        resetFilterObject();
    }, [dimensionConfigData?.defaultDim1DropdownItem?.value]);

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const newApplyFilterObj: any = {};
        scenarioFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(scenarioFilterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(_.cloneDeep(newApplyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    };

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const filters = transformFilterObjectWithDimensionsInfo(
                tempApplyFilterObject,
                dimensionConfigData?.scenarioFilterKeys ?? [],
            );
            return {
                attribute_type: selectedFilter.value,
                limit: scenarioFilterOffsetLimit,
                filters,
                scenario_id: selectedScenarioInfo?.scenarioId || 0,
            };
        }
    }, [selectedFilter]);

    const dataCountVars = useMemo(() => {
        const filters = transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);
        return { filters, scenario_id: selectedScenarioInfo?.scenarioId };
    }, [applyFilterObject, selectedScenarioInfo?.scenarioId]);

    const { loading: dataCountLoading } = useCommonApiHandler({
        url: pricingSimulatorUrls.getScenarioDataCount,
        method: 'post',
        queryKey: ['get_scenario_data_count', dataCountVars],
        queryConfig: { enabled: !!selectedScenarioInfo?.scenarioId && open },
        variables: { ...dataCountVars },
        parserFunction: (res) => {
            if (res.count > res.threshold) {
                setErrorMsg(
                    `Row limit exceeded (${res.threshold}). Current row count is ${res.count}. Please select appropriate filter.`,
                );
            } else {
                setErrorMsg('');
            }
            return res;
        },
    });

    const downloadTemplate = async (payload: any) => {
        sendNotification(
            'info',
            'Your template is being prepared. This may take a few moments. Thank you for your patience!',
        );
        const response = await axios.post(
            `${pricingSimulatorUrls.downloadTemplate}`,
            payload, // Send payload with request
            { responseType: 'blob' }, // Ensure the response is a blob
        );
        return response.data;
    };

    const downloadTemplateMutation = useMutation({
        mutationFn: downloadTemplate,
        onSuccess: (blob: any) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'scenario_template.xlsx'); // Set file name
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            sendNotification('success', 'Your template has been prepared and the download has started successfully.');
        },
        onError: (err: any) => {
            sendNotification('error', 'An error occurred while preparing your template. Please try again later.');
            console.error('Error while downloading:', err);
        },
    });

    const handleDownloadClick = () => {
        const filters = transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);
        const payload = {
            filters,
            scenario_id: selectedScenarioInfo?.scenarioId,
            dimensions: selectedDims,
            metrics: selectedMetrics,
            project_id: selectedScenarioInfo?.projectId,
        };
        downloadTemplateMutation.mutateAsync(payload);
        setSelectedDims(fixedDims);
        setSelectedMetrics([]);
        resetFilterObject();
        onCancel();
    };

    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{
                    htmlType: 'submit',
                    disabled: !!errorMsg || !selectedMetrics.length || dataCountLoading,
                    style: { borderRadius: 6, minWidth: '140px' },
                    className: 'disabled:bg-red-500 disabled:opacity-50',
                }}
                okText={'Download Template'}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={handleDownloadClick}
                open={open}
                onCancel={() => {
                    setSelectedDims(fixedDims);
                    setSelectedMetrics([]);
                    resetFilterObject();
                    onCancel();
                }}
                title="Bulk Update: Download Template"
                width={500}
            >
                <div className="px-3">
                    <div className="border-backgroundGreyAlt1 border rounded-sm px-3 py-6">
                        <div className="mb-4">
                            <h2>Download Template</h2>
                            <p>
                                Apply filters and select the required dimensions and metrics to generate your template.
                                Once done, download the Excel file to start editing KPIs.
                            </p>
                        </div>
                        <div className="flex justify-between">
                            <NewGlobalFilter
                                availableFilters={availableFilters}
                                allCheckedAttribute={allCheckedAttribute}
                                allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                                applyFilterObject={applyFilterObject}
                                resetFilterObject={resetFilterObject}
                                onSubmitHandler={onFilterSaveClickHandler}
                                url={pricingSimulatorUrls.getFilterAttributes}
                                selectedFilter={selectedFilter}
                                setSelectedFilter={setSelectedFilter}
                                tempApplyFilterObject={tempApplyFilterObject}
                                setTempApplyFilterObject={setTempApplyFilterObject}
                                apiVariables={commonVariables}
                                filterDimensionInfo={scenarioFilterDimensionInfo}
                            />
                            {dataCountLoading && <Spin indicator={antIcon} />}
                        </div>
                        {/*  Filter Ribbon  */}
                        <div className="my-4">
                            <FilterRibbon
                                allCheckedAttribute={allCheckedAttribute}
                                allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                                applyFilterObject={applyFilterObject}
                                resetFilterObject={resetFilterObject}
                                onSubmitHandler={(obj: any) => {
                                    setAllCheckedAttribute(obj.allCheckedAttribute);
                                    setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                                    setApplyFilterObject(obj.applyFilterObject);
                                }}
                                parentClassName=""
                            />
                            {errorMsg && <span className="text-redAlt text-xs mt-1">{errorMsg}</span>}
                        </div>
                        <div className="my-4">
                            <span className="text-xs text-fontGreyAlt">Selected Dimensions</span>
                            <SelectWithInfiniteScroller
                                style={{ width: '100%', border: `1px ${colorConstants.greyAlt}` }}
                                mode="multiple"
                                placeholder={'Dimensions'}
                                className="dropdown-height dropdown-width-alt "
                                options={dimDropdownList}
                                value={selectedDims}
                                filterOption={false}
                                tagRender={(props) => {
                                    return (
                                        <Tag
                                            style={{ marginRight: 4 }}
                                            closable={!fixedDims.includes(props.value)}
                                            onClose={(event) => {
                                                event.preventDefault();
                                                setSelectedDims(selectedDims.filter((val) => val !== props.value));
                                            }}
                                        >
                                            {props?.label}
                                        </Tag>
                                    );
                                }}
                                maxTagCount="responsive"
                                onChange={(values) => {
                                    const updatedValues = Array.from(new Set([...fixedDims, ...values]));
                                    setSelectedDims(updatedValues);
                                    return;
                                }}
                            />
                        </div>
                        <div className="my-4">
                            <span className="text-xs text-fontGreyAlt">Selected Metrics</span>
                            <SelectWithInfiniteScroller
                                style={{ width: '100%', border: `1px ${colorConstants.greyAlt}` }}
                                mode="multiple"
                                placeholder={'Metrics'}
                                className="dropdown-height dropdown-width-alt "
                                options={metrics}
                                value={selectedMetrics}
                                filterOption={false}
                                tagRender={(props) => {
                                    return (
                                        <Tag
                                            style={{ marginRight: 4 }}
                                            closable
                                            onClose={(event) => {
                                                event.preventDefault();
                                                setSelectedMetrics(
                                                    selectedMetrics.filter((val) => val !== props.value),
                                                );
                                            }}
                                        >
                                            {props?.label}
                                        </Tag>
                                    );
                                }}
                                maxTagCount="responsive"
                                onChange={(values) => {
                                    if (values.length > 3) {
                                        sendNotification('error', 'Upto 3 Metrics allowed');
                                        return;
                                    }
                                    setSelectedMetrics([...values]);
                                    return;
                                }}
                            />
                        </div>
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

export default DownloadTemplateModal;
